import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const schema = z.object({
  serial: z.string().min(1, 'errors.validation.serialRequired'),
  reason: z
    .string()
    .min(1, 'errors.validation.reasonRequired')
    .max(280, 'errors.validation.reasonTooLong'),
});

type FormValues = z.infer<typeof schema>;

interface AddBlacklistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: FormValues) => void;
  isLoading?: boolean;
  serverError?: string | null;
}

export default function AddBlacklistDialog({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
  serverError,
}: AddBlacklistDialogProps) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  React.useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('blacklist.addDialog.title')}</DialogTitle>
          <DialogDescription>{t('blacklist.addDialog.description')}</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={handleSubmit((data) => {
            onSubmit(data);
          })}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="serial">{t('blacklist.addDialog.serial')}</Label>
            <Input id="serial" autoComplete="off" className="font-mono" {...register('serial')} />
            {errors.serial && (
              <p className="text-sm text-destructive" role="alert">
                {t(String(errors.serial.message))}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="reason">{t('blacklist.addDialog.reason')}</Label>
            <textarea
              id="reason"
              rows={3}
              {...register('reason')}
              className="flex min-h-[84px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.reason && (
              <p className="text-sm text-destructive" role="alert">
                {t(String(errors.reason.message))}
              </p>
            )}
          </div>
          {serverError && (
            <p className="text-sm text-destructive" role="alert" aria-live="polite">
              {serverError}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('blacklist.addDialog.adding')}
                </>
              ) : (
                t('blacklist.addDialog.add')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
